import { Select, Text, toast } from "@medusajs/ui"
import { useEffect, useState } from "react"
import { Field } from "@/components/form"
import { ErrorState, SaveBar, Section } from "@/components/ui"
import { useCan } from "@/lib/permissions"
import { errorMessage } from "@/lib/sdk"
import { useCheckoutSettings, useSaveCheckoutSettings, type CheckoutSettings, type FieldMode } from "@/lib/settings"
import { SettingsTitle } from "./settings-general"

const ROWS: { key: "company" | "address_2" | "phone"; label: string; hint: string }[] = [
  { key: "company", label: "Company name", hint: "Shown above the address on the checkout." },
  { key: "address_2", label: "Address line 2 (apartment, suite, etc.)", hint: "Second address line." },
  { key: "phone", label: "Shipping address phone number", hint: "Carriers often need a phone number for delivery." },
]
const LABEL: Record<FieldMode, string> = { hidden: "Don't include", optional: "Optional", required: "Required" }

/** Settings → Checkout → Customer information. */
export function CheckoutSettingsPage() {
  const { data, error } = useCheckoutSettings()
  const save = useSaveCheckoutSettings()
  const writable = useCan()("settings:write")
  const [f, setF] = useState<CheckoutSettings | null>(null)
  useEffect(() => { if (data) setF(data.settings) }, [data])
  if (error) return <ErrorState error={error} />
  if (!f || !data) return <Text size="small">Loading…</Text>
  const dirty = JSON.stringify(f) !== JSON.stringify(data.settings)
  return (
    <div className="flex flex-col gap-4 pb-24">
      <SettingsTitle title="Checkout" subtitle="Choose which details customers are asked for at checkout." />
      <Section title="Customer information">
        <div className="flex flex-col gap-4 p-4">
          {ROWS.map((r) => (
            <Field key={r.key} label={r.label} hint={r.hint}>
              <Select value={f[r.key]} disabled={!writable} onValueChange={(v) => setF({ ...f, [r.key]: v as FieldMode })}>
                <Select.Trigger aria-label={r.label}><Select.Value /></Select.Trigger>
                <Select.Content>
                  {(Object.keys(LABEL) as FieldMode[]).map((m) => <Select.Item key={m} value={m}>{LABEL[m]}</Select.Item>)}
                </Select.Content>
              </Select>
            </Field>
          ))}
          <Text size="xsmall" className="text-ui-fg-subtle">Email, name, country/region, street and city are always required, plus the state/province and postal code in countries/regions that use them.</Text>
        </div>
      </Section>
      <Section title="Abandoned checkouts">
        <div className="flex flex-col gap-4 p-4">
          <Field label="Send abandoned checkout emails" hint="Customers who entered their email but didn't finish get one reminder with a link back to their cart. You can also send emails by hand from Orders → Abandoned checkouts.">
            <Select value={String(f.abandoned_email_hours)} disabled={!writable} onValueChange={(v) => setF({ ...f, abandoned_email_hours: Number(v) as CheckoutSettings["abandoned_email_hours"] })}>
              <Select.Trigger aria-label="Send abandoned checkout emails"><Select.Value /></Select.Trigger>
              <Select.Content>
                {([[0, "Don't send"], [1, "1 hour after abandonment"], [6, "6 hours after abandonment"], [10, "10 hours after abandonment"], [24, "24 hours after abandonment"]] as const).map(([h, l]) => <Select.Item key={h} value={String(h)}>{l}</Select.Item>)}
              </Select.Content>
            </Select>
          </Field>
        </div>
      </Section>
      <SaveBar visible={writable && dirty} message="Unsaved changes" onDiscard={() => setF(data.settings)} onSave={() => save.mutate(f, { onSuccess: () => toast.success("Checkout settings saved"), onError: (e) => toast.error(errorMessage(e)) })} saving={save.isPending} />
    </div>
  )
}
